import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { z } from "zod";
import { toast } from "sonner";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { getAllClass } from "../../../api/pedagogicElement";
import { getAllStudentByYear } from "../../../api/etudiants";
import { getStudentsByPaymentPlan } from "../../../api/paiementPlans";
import { assignPayersToPlan } from "../../../api/studentPaiementPlan";
import withPermission from "../../../core/common/autorisations/withPermission";

type ClassItem = {
  id: string;
  label: string;
  Level?: { abbreviation?: string; label?: string };
};
type StudentItem = {
  id?: string;
  firstname?: string;
  lastname?: string;
  matricule?: string;
  Level?: { abbreviation?: string };
  Registrations?: Array<{
    Class?: { Level?: { abbreviation?: string }; label?: string };
  }>;
  StudentProfil?: {
    id?: string;
    firstname?: string;
    lastname?: string;
    matricule?: string;
  };
};

type AssignPayload = {
  assigned_date: string;
  id_paymentPlans: string;
  students: { to_create: String[]; to_delete: String[] };
};

type Props = {
  show: boolean;
  paymentPlan: any;
  assignedStudentIds?: string[];
  onClose: () => void;
  onSaved?: () => void;
};

const payloadSchema = z.object({
  paymentPlanId: z.string().min(1, "Plan de paiement requis"),
  studentIds: z.array(z.string()).min(1, "Sélectionnez au moins un étudiant"),
});
type PayloadFields = z.infer<typeof payloadSchema>;

const AssignPayersModal = ({ show, paymentPlan, onClose, onSaved }: Props) => {
  const { selectedYear } = useAcademicYear();

  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [expandedClassId, setExpandedClassId] = useState<string>("");
  const [allStudents, setAllStudents] = useState<StudentItem[]>([]);
  const [initialAssignedStudentIds, setInitialAssignedStudentIds] = useState<
    any[]
  >([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [classQuery, setClassQuery] = useState("");
  const [studentQuery, setStudentQuery] = useState("");

  // fonction pour récupérer toutes les classes de l'année academique
  const fetchClasses = async () => {
    try {
      const response = await getAllClass(
        selectedYear?.id,
        buildPermissionContext(Resource.STUDENT_PAYMENT_PLAN, Action.READ),
      );
      return response.data || [];
    } catch (error: any) {
      // console.log("Erreur lors du cargemetn des classes: ", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des classes",
      );
    }
  };
  // fonction pour récupérer tous les étudiants de l'année academique
  const fetchStudentsForYear = async () => {
    try {
      if (!selectedYear?.id) return [];
      const response = await getAllStudentByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.STUDENT_PAYMENT_PLAN, Action.READ),
      );
      return response.data || [];
    } catch (error: any) {
      // console.log("Erreur lors du cargemetn des étudiants: ", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des étudiants",
      );
    }
  };

  // fonction pour récupérer les étudiants affectés au plan de paiement
  const fetchAssignedStudents = async () => {
    try {
      if (!paymentPlan?.id) return [];
      const response = await getStudentsByPaymentPlan(
        paymentPlan?.id,
        buildPermissionContext(Resource.STUDENT_PAYMENT_PLAN, Action.READ),
      );
      const initialStudentAssignedIds =
        response.data.map((s: any) => s?.id_studentProfil) || [];
      // console.log("Étudiants affectés au plan: ", initialStudentAssignedIds);
      setInitialAssignedStudentIds(initialStudentAssignedIds);
      // return response.data || [];
      setSelectedStudentIds(initialStudentAssignedIds);
    } catch (error: any) {
      // console.log(
      //   "Erreur lors du chargement des étudiants affectés au plan: ",
      //   error,
      // );
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des étudiants affectés au plan",
      );
    }
  };

  // fonction pour charger les étudiant de cette année
  const load = async () => {
    if (!selectedYear?.id) return;
    setLoadingClasses(true);
    setLoadingStudents(true);
    try {
      const [classesData, studentsData] = await Promise.all([
        fetchClasses(),
        fetchStudentsForYear(),
      ]);
      const sorted =
        classesData.slice().sort((a: any, b: any) => {
          const l = (a?.Level?.label || "").localeCompare(
            b?.Level?.label || "",
            "fr",
            { numeric: true },
          );
          if (l !== 0) return l;
          return (a.label || "").localeCompare(b.label || "", "fr", {
            numeric: true,
          });
        }) || [];
      setClasses(sorted);
      setAllStudents(studentsData || []);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des classes",
      );
    } finally {
      setLoadingClasses(false);
      setLoadingStudents(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    fetchAssignedStudents();
    load();
  }, [show, selectedYear]);

  const belongsToClass = (s: any, classId: string) => {
    const cls = classes.find((c) => String(c.id) === String(classId));
    if (!cls) return false;
    const levelAbbr =
      s?.Registrations?.[0]?.Class?.Level?.abbreviation ??
      s?.Level?.abbreviation;
    const classLabel = s?.Registrations?.[0]?.Class?.label;
    return (
      (levelAbbr || "") === (cls?.Level?.abbreviation || "") &&
      (classLabel || "") === (cls?.label || "")
    );
  };

  const toggleClassSelection = async (classId: string) => {
    const studs = allStudents.filter((s) => belongsToClass(s, classId));
    const ids = studs.map((s) => String(s?.StudentProfil?.id ?? s?.id));
    const allSelected = ids.every((id) => selectedStudentIds.includes(id));
    const next = [...selectedStudentIds];
    if (allSelected) {
      ids.forEach((id) => next.splice(next.indexOf(id), 1));
    } else {
      ids.forEach((id) => next.push(id));
    }
    setSelectedStudentIds(next);
  };

  const toggleStudent = (sid: string) => {
    const next = [...selectedStudentIds];
    if (next.includes(sid)) next.splice(next.indexOf(sid), 1);
    else next.push(sid);
    setSelectedStudentIds(next);
  };

  const toggleExpandClass = async (classId: string) => {
    if (expandedClassId === classId) {
      setExpandedClassId("");
      return;
    }
    setExpandedClassId(classId);
  };

  const filteredClasses = useMemo(
    () =>
      classes.filter((c: any) =>
        `${c?.Level?.abbreviation || ""} ${c?.label || ""}`
          .toLowerCase()
          .includes(classQuery.toLowerCase()),
      ),
    [classes, classQuery],
  );

  const visibleStudents = useMemo(() => {
    const studs = expandedClassId
      ? allStudents.filter((s) => belongsToClass(s, expandedClassId))
      : [];
    return studs.filter((s) =>
      `${s?.StudentProfil?.firstname || s?.firstname || ""} ${
        s?.StudentProfil?.lastname || s?.lastname || ""
      }`
        .toLowerCase()
        .includes(studentQuery.toLowerCase()),
    );
  }, [allStudents, expandedClassId, studentQuery, classes]);

  const selectedCount = selectedStudentIds.length;

  const classCounts = (classId: string) => {
    const studs = allStudents.filter((s) => belongsToClass(s, classId)) || [];
    const ids = studs.map((s) => String(s?.StudentProfil?.id ?? s?.id));
    const selected = ids.filter((id) => selectedStudentIds.includes(id)).length;
    const total = ids.length;
    return { selected, total };
  };

  const classIsFullySelected = (classId: string) => {
    const studs = allStudents.filter((s) => belongsToClass(s, classId)) || [];
    if (studs.length === 0) return false;
    const ids = studs.map((s) => String(s?.StudentProfil?.id ?? s?.id));
    return ids.every((id) => selectedStudentIds.includes(id));
  };

  const onSubmit = async () => {
    const payload: AssignPayload = {
      assigned_date: new Date().toISOString(),
      id_paymentPlans: paymentPlan?.id,
      students: {
        to_create: Array.from(
          selectedStudentIds.filter(
            (id) => !initialAssignedStudentIds.includes(id),
          ),
        ),
        to_delete: initialAssignedStudentIds.filter(
          (id) => !selectedStudentIds.includes(id),
        ),
      },
    };

    try {
      console.log("Payload inside try catch: ", payload);
      const resp = await assignPayersToPlan(payload);
      console.log("REsponse: ", resp);
      toast.success("Payeurs affectés avec succès");
      onSaved?.();
      onClose();
    } catch (error: any) {
      console.log("Erreur lors de l'affectation des payeurs: ", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Échec de l'affectation des payeurs",
      );
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="xl">
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title>
          Affectation des payeurs - {paymentPlan?.label}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-2 p-2 rounded bg-white">
              <div className="w-100 d-flex align-items-center">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Rechercher une classe"
                  value={classQuery}
                  onChange={(e) => setClassQuery(e.target.value)}
                />
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "0.5rem",
                maxHeight: 520,
                overflowY: "auto",
              }}
            >
              {loadingClasses ? (
                <div className="text-muted small text-center">
                  Chargement...
                </div>
              ) : filteredClasses.length === 0 ? (
                <div className="text-muted small text-center">
                  Aucune classe
                </div>
              ) : (
                filteredClasses.map((cls: any) => {
                  const fullySelected = classIsFullySelected(String(cls.id));
                  const counts = classCounts(String(cls.id));
                  return (
                    <div key={cls.id}>
                      <div
                        className="card flex-fill bg-warning-transparent-subtle shadow-md"
                        style={{
                          minHeight: 120,
                          cursor: "pointer",
                          transition: "all 200ms ease",
                        }}
                        onClick={() => toggleClassSelection(String(cls.id))}
                        onDoubleClick={() => toggleExpandClass(String(cls.id))}
                      >
                        <div
                          className={`card-body p-3 d-flex flex-column justify-content-between align-items-center ${fullySelected ? "border-primary" : ""} border border-2`}
                        >
                          <div className="d-flex align-items-center">
                            <span className="avatar avatar-sm flex-shrink-0 bg-info-subtle d-flex align-items-center justify-content-center rounded-circle">
                              <i className="ti ti-school text-info" />
                            </span>
                            <div className="ms-2">
                              <div
                                className="fw-bold small"
                                style={{ wordBreak: "break-word" }}
                              >
                                {cls?.Level?.abbreviation}
                              </div>
                              <div
                                className="text-muted small"
                                style={{ wordBreak: "break-word" }}
                              >
                                {cls.label}
                              </div>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary-subtle text-primary border border-2">
                              {counts.selected}/{counts.total} sélectionnés
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-2 p-2 rounded bg-white">
              <div className="w-100 d-flex align-items-center">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Rechercher un étudiant"
                  value={studentQuery}
                  onChange={(e) => setStudentQuery(e.target.value)}
                  disabled={!expandedClassId}
                />
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
                gap: "0.1rem",
                maxHeight: 520,
                overflowY: "auto",
              }}
            >
              {!expandedClassId ? (
                <div className="text-muted small text-center">
                  Double-cliquez sur une classe pour afficher ses étudiants
                </div>
              ) : loadingStudents ? (
                <div className="text-muted small text-center">
                  Chargement...
                </div>
              ) : visibleStudents.length === 0 ? (
                <div className="text-muted small text-center">
                  Aucun étudiant
                </div>
              ) : (
                visibleStudents.map((s: any) => {
                  const sid = String(s?.StudentProfil?.id ?? s?.id);
                  const firstname = s?.StudentProfil?.firstname ?? s?.firstname;
                  const lastname = s?.StudentProfil?.lastname ?? s?.lastname;
                  const levelAbbr =
                    s?.Registrations?.[0]?.Class?.Level?.abbreviation ??
                    s?.Level?.abbreviation;
                  const classLabel = s?.Registrations?.[0]?.Class?.label;
                  const selected = selectedStudentIds.includes(sid);
                  return (
                    <div key={sid}>
                      <div
                        className={`card flex-fill ${selected ? "bg-success-transparent-subtle border-success border" : "bg-white border"} border-2 shadow-md`}
                        style={{
                          cursor: "pointer",
                          transition: "all 200ms ease",
                        }}
                        onClick={() => toggleStudent(sid)}
                      >
                        <div className="card-body d-flex align-items-center">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selected}
                              readOnly
                            />
                          </div>
                          <div className="flex-grow-1 ms-2 d-flex flex-column">
                            <div className="d-flex align-items-center mb-1">
                              <span className="avatar avatar-sm flex-shrink-0 bg-success-subtle d-flex align-items-center justify-content-center rounded-circle me-2">
                                <i className="ti ti-user text-success" />
                              </span>
                              <div>
                                <div
                                  className="fw-semibold small"
                                  style={{ wordBreak: "break-word" }}
                                >
                                  {firstname} {lastname}
                                </div>
                                <div className="text-muted small">
                                  {(s?.StudentProfil?.matricule ||
                                    s?.matricule) ??
                                    ""}
                                </div>
                              </div>
                            </div>
                            <div className="text-muted small ms-1">
                              {levelAbbr} - {classLabel}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between border-top">
        <div className="small fw-semibold">
          {selectedCount} étudiant(s) sélectionné(s)
        </div>
        <div>
          <button
            type="button"
            className="btn btn-danger me-2"
            onClick={() => {
              setExpandedClassId("");
              onClose();
            }}
          >
            Fermer
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={onSubmit}
            // disabled={selectedCount === 0}
          >
            Affecter les payeurs
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default withPermission(
  AssignPayersModal,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.STUDENT_PAYMENT_PLAN, action),
    ),
  ],
  "masquer",
);
